import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getStats, getCustomers, getProjects } from '../services/api';
import { 
  Users, 
  FolderOpen, 
  FileText, 
  TrendingUp, 
  Plus, 
  ArrowRight,
  Clock,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DashboardPage = () => {
  const { canCreate, canUpload } = useAuth();
  const [stats, setStats] = useState({ customers: 0, projects: 0, documentation: 0, recent_activity: [] });
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, customersRes, projectsRes] = await Promise.all([
        getStats(),
        getCustomers(),
        getProjects()
      ]);
      setStats(statsRes.data);
      setRecentCustomers(customersRes.data.slice(0, 5));
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: 'Customers', 
      value: stats.customers, 
      icon: Users, 
      color: 'bg-blue-50 text-[#2B8FD8]',
      href: '/customers'
    },
    { 
      label: 'Projects', 
      value: stats.projects, 
      icon: FolderOpen, 
      color: 'bg-emerald-50 text-emerald-600',
      href: '/projects'
    },
    { 
      label: 'Documentation', 
      value: stats.documentation, 
      icon: FileText, 
      color: 'bg-amber-50 text-amber-600',
      href: '/documentation'
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Overview of your integration documentation</p>
        </div>

        {/* User Guide Banner */}
        <div className="mb-6 rounded-xl bg-gradient-to-r from-[#1e3a5f] to-[#2B8FD8] p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">New to CloudFactory Docs Hub?</p>
              <p className="text-blue-200 text-xs mt-0.5">Read our comprehensive user guide — step-by-step instructions for all features.</p>
            </div>
          </div>
          <Link to="/userguide">
            <button className="shrink-0 bg-white text-[#2B8FD8] font-semibold text-sm px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1.5">
              <BookOpen className="h-4 w-4" />
              User Guide
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link key={stat.label} to={stat.href}>
                <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" data-testid={`stat-${stat.label.toLowerCase()}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${stat.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canCreate && (
                <Link to="/customers/new">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-add-customer">
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Customer
                  </Button>
                </Link>
              )}
              {canCreate && (
                <Link to="/projects/new">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-add-project">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Project
                  </Button>
                </Link>
              )}
              {canUpload && (
                <Link to="/documentation/upload">
                  <Button className="w-full justify-start bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="quick-upload-doc">
                    <Plus className="h-4 w-4 mr-2" />
                    Upload Documentation
                  </Button>
                </Link>
              )}
              {!canCreate && !canUpload && (
                <p className="text-sm text-slate-500 py-2">
                  You have read-only access. Contact an admin to request editor permissions.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recent_activity && stats.recent_activity.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_activity.map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600">{activity.title}</span>
                      <span className="text-xs text-slate-400 ml-auto">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No recent activity</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Lists */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Customers */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">Recent Customers</CardTitle>
              <Link to="/customers">
                <Button variant="ghost" size="sm" className="text-[#2B8FD8]">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentCustomers.length > 0 ? (
                <div className="space-y-3">
                  {recentCustomers.map((customer) => (
                    <Link 
                      key={customer.id} 
                      to={`/customers/${customer.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{customer.name}</p>
                        <p className="text-sm text-slate-500">{customer.project_count} projects</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No customers yet</p>
                  {canCreate && (
                    <Link to="/customers/new">
                      <Button size="sm" className="bg-[#2B8FD8] hover:bg-[#2075b3]">
                        <Plus className="h-4 w-4 mr-1" /> Add Customer
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Projects */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">Recent Projects</CardTitle>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-[#2B8FD8]">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentProjects.length > 0 ? (
                <div className="space-y-3">
                  {recentProjects.map((project) => (
                    <Link 
                      key={project.id} 
                      to={`/projects/${project.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{project.name}</p>
                        <p className="text-sm text-slate-500">{project.customer_name}</p>
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                        {project.documentation_count} docs
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No projects yet</p>
                  {canCreate && (
                    <Link to="/projects/new">
                      <Button size="sm" className="bg-[#2B8FD8] hover:bg-[#2075b3]">
                        <Plus className="h-4 w-4 mr-1" /> Create Project
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
